import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import BreadCrumb from '../../../_metronic/layout/components/breadcrumb'
import { useIntl } from 'react-intl'
import { useState } from 'react'
import PasswordChecklist from 'react-password-checklist'
import { useChangePassword } from 'src/feature/auth/context/useMutation'
import { useAuthUser } from 'react-auth-kit'

export default function Profile() {
  const intl = useIntl()
  const userAuthData = useAuthUser()

  const [passwordShown, setPasswordShown] = useState(false)
  const [passwordConfirmShown, setPasswordConfirmShown] = useState(false)
  const [passwordValid, setPasswordValid] = useState(false)

  const { mutateAsync, isLoading } = useChangePassword()

  const validationSchema = Yup.object({
    oldPassword: Yup.string().required(intl.formatMessage({ id: 'GLOBAL.VALIDATION.REQUIRED_FIELD' })),
    password: Yup.string().required(' ').min(6, ' ').max(50, ' '),
    confirmPassword: Yup.string()
      .required(' ')
      .oneOf([Yup.ref('password'), ''], ' '),
  })

  const handleSubmit = async (values) => {
    const email = userAuthData().email

    if (!passwordValid) return

    await mutateAsync({ values, email })
  }


  return (
    <>
      <BreadCrumb currentPage={intl.formatMessage({ id: 'CHANGE_PASSWORD' })} />
      <div className='card w-100 my-10'>
        <div className='card-header'>
          <h3 className='card-title'>
            {intl.formatMessage({ id: 'CHANGE_PASSWORD' })}
          </h3>
        </div>
        <div className='card-body'>
          <Formik
            initialValues={{
              oldPassword: '',
              password: '',
              confirmPassword: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched }) => (
              <Form>
                <div className='fv-row mb-7 form-floating'>
                  {/* eye icon */}
                  <div
                    className='position-absolute top-50 end-0 translate-middle-y fs-10 pe-6'
                    style={{ cursor: 'pointer' }}
                    onClick={() => setPasswordShown(!passwordShown)}
                  >
                    <i
                      className={`bi ${passwordShown ? 'bi-eye-slash' : 'bi-eye'}`}
                      style={{ fontSize: '1.4rem' }}
                    ></i>
                  </div>
                  <Field
                    name='oldPassword'
                    type={passwordShown ? 'text' : 'password'}
                    className='form-control'
                    placeholder=''
                  />
                  <label>
                    {/* Password */}
                    {intl.formatMessage({ id: 'FORGOT.OLD_PASSWORD' })}
                  </label>
                  {errors.oldPassword && touched.oldPassword ? (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{errors.oldPassword}</div>
                    </div>
                  ) : null}
                </div>

                <div className='fv-row mb-7 form-floating'>
                  {/* eye icon */}
                  <div
                    className='position-absolute top-50 end-0 translate-middle-y fs-10 pe-6'
                    style={{ cursor: 'pointer' }}
                    onClick={() => setPasswordShown(!passwordShown)}
                  >
                    <i
                      className={`bi ${passwordShown ? 'bi-eye-slash' : 'bi-eye'}`}
                      style={{ fontSize: '1.4rem' }}
                    ></i>
                  </div>
                  <Field
                    name='password'
                    type={passwordShown ? 'text' : 'password'}
                    className='form-control'
                    placeholder=''
                  />
                  <label>
                    {/* Password */}
                    {intl.formatMessage({ id: 'FORGOT.PASSWORD' })}
                  </label>
                  {errors.password && touched.password ? (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{errors.password}</div>
                    </div>
                  ) : null}
                </div>

                <div className='fv-row mb-4 form-floating'>
                  <div
                    className='position-absolute top-50 end-0 translate-middle-y fs-10 pe-6'
                    style={{ cursor: 'pointer' }}
                    onClick={() => setPasswordConfirmShown(!passwordConfirmShown)}
                  >
                    <i
                      className={`bi ${passwordConfirmShown ? 'bi-eye-slash' : 'bi-eye'}`}
                      style={{ fontSize: '1.4rem' }}
                    ></i>
                  </div>
                  <Field
                    name='confirmPassword'
                    type={passwordConfirmShown ? 'text' : 'password'}
                    className='form-control'
                    placeholder=''
                  />
                  <label>
                    {/* Re-Password */}
                    {intl.formatMessage({ id: 'FORGOT.CONFIRM_PASSWORD' })}
                  </label>
                  {errors.confirmPassword && touched.confirmPassword ? (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{errors.confirmPassword}</div>
                    </div>
                  ) : null}
                </div>
                <div>
                  <PasswordChecklist
                    className='mb-7'
                    rules={[
                      'minLength',
                      'specialChar',
                      'number',
                      'capital',
                      'lowercase',
                      'match',
                    ]}
                    minLength={6}
                    value={values.password}
                    valueAgain={values.confirmPassword}
                    onChange={(isValid) => {
                      setPasswordValid(isValid)
                    }}
                    messages={{
                      minLength: intl.formatMessage({
                        id: 'FORGOT.PASSWORD.RULES.MIN_LENGTH',
                      }),
                      // specialChar: 'At least one special character (e.g. !@#$%^&*)',
                      // number: 'At least one number (e.g. 0-9)',
                      // capital: 'At least one capital letter (e.g. A-Z)',
                      // lowercase: 'At least one lowercase letter (e.g. a-z)',
                      // match: 'Passwords must match',
                      specialChar: intl.formatMessage({ id: 'FORGOT.PASSWORD.RULES.SPECIAL_CHAR' }),
                      number: intl.formatMessage({ id: 'FORGOT.PASSWORD.RULES.NUMBER' }),
                      capital: intl.formatMessage({ id: 'FORGOT.PASSWORD.RULES.CAPITAL' }),
                      lowercase: intl.formatMessage({ id: 'FORGOT.PASSWORD.RULES.LOWERCASE' }),
                      match: intl.formatMessage({ id: 'FORGOT.PASSWORD.RULES.MATCH' }),
                    }}
                  />
                </div>

                <div className='d-flex flex-stack'>
                  <div className='m-0'>
                    <button disabled={isLoading} className='btn btn-primary me-2'>
                      {isLoading ? (
                        <span data-kt-translate='general-progress'>
                          {intl.formatMessage({ id: 'LOADING' })}
                        </span>
                      ) : (
                        <>
                          <i className='bi bi-arrow-right me-3 fs-3'></i>
                          {intl.formatMessage({ id: 'GLOBAL.BUTTON.SUBMIT' })}
                        </>
                      )}
                    </button>

                    <a href='/dashboard' className='btn fw-bold'>
                      {intl.formatMessage({ id: 'GLOBAL.BUTTON.CANCEL' })}
                    </a>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  )
}
